import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Phone, MessageCircle, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function BookingSuccess() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("pending");
  const [owner, setOwner] = useState(null);
  const [renterKyc, setRenterKyc] = useState(null);
  const [kycStatus, setKycStatus] = useState("idle");
  const [docType, setDocType] = useState("national_id");
  const [file, setFile] = useState(null);
  const [waitingLong, setWaitingLong] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    const poll = async (n = 0) => {
      if (cancelled.current) return;
      // MOMO/Airtel approvals can take a while (the renter needs to unlock
      // their phone and confirm) — keep polling for several minutes rather
      // than silently giving up. After ~90s, tell the user we're still
      // waiting instead of just showing the same unchanged spinner.
      if (n === 40) setWaitingLong(true);
      if (n > 150) { setStatus("pending"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setRenterKyc(data.renter_kyc_status);
          if (data.owner) setOwner(data.owner);
          return;
        }
        if (data.payment_status === "failed") { setStatus("failed"); return; }
      } catch(e) {}
      setTimeout(() => poll(n + 1), 2200);
    };
    poll();
    return () => { cancelled.current = true; };
  }, [sessionId]);

  const handleKycUpload = async () => {
    if (!file) { toast.error("Please select a document"); return; }
    setKycStatus("uploading");
    try {
      const formData = new FormData();
      formData.append("document_type", docType);
      formData.append("file", file);
      await api.post("/kyc/verify-document", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setKycStatus("done");
      setRenterKyc("verified");
      toast.success("Identity verified!");
      // Fetch owner contact now
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.owner) setOwner(data.owner);
    } catch(e) {
      toast.error(formatApiError(e));
      setKycStatus("error");
    }
  };

  const whatsappLink = owner?.phone
    ? `https://wa.me/${owner.phone.replace(/[^0-9]/g, "")}`
    : null;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-xl px-4 py-20">
        <Card className="border-border shadow-organic p-10 text-center">

          {status === "pending" && (
            <>
              <Loader2 className="mx-auto animate-spin text-primary" size={36} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Verifying payment…</h1>
              <p className="text-sm text-muted-foreground mt-2">
                {waitingLong
                  ? "Still waiting for you to approve the payment on your phone. Please keep this page open — it can take a minute or two."
                  : "Hang tight, this usually takes a few seconds."}
              </p>
            </>
          )}

          {status === "paid" && renterKyc !== "verified" && kycStatus !== "done" && (
            <>
              <CheckCircle2 className="mx-auto text-primary" size={42} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Booking fee paid!</h1>
              <p className="text-sm text-muted-foreground mt-2 mb-6">
                Upload your ID to confirm the booking and unlock the owner contact.
              </p>
              <div className="text-left space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-1.5">Document type</label>
                  <div className="flex gap-2 flex-wrap">
                    {[["national_id","National ID"],["passport","Passport"],["driving_licence","Driving Licence"]].map(([val,label]) => (
                      <button key={val} type="button" onClick={() => setDocType(val)}
                        className={"px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + (docType === val ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted")}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-1.5">Upload document</label>
                  <div className={"border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors " + (file ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}
                    onClick={() => document.getElementById("kyc-file-reg").click()}>
                    <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
                    {file ? (
                      <p className="text-sm font-medium text-primary">{file.name}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Click to upload photo or PDF</p>
                    )}
                    <input id="kyc-file-reg" type="file" accept="image/*,.pdf" className="hidden"
                      onChange={e => setFile(e.target.files[0])} />
                  </div>
                </div>
                <Button onClick={handleKycUpload} disabled={kycStatus === "uploading" || !file}
                  className="w-full h-11 bg-primary hover:bg-primary/90">
                  {kycStatus === "uploading" ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Uploading…</>
                  ) : (
                    <><ShieldCheck size={16} className="mr-2" /> Verify my identity</>
                  )}
                </Button>
                {kycStatus === "error" && <p className="text-xs text-destructive text-center">Upload failed. Please try again.</p>}
              </div>
            </>
          )}

          {status === "paid" && (renterKyc === "verified" || kycStatus === "done") && (
            <>
              <CheckCircle2 className="mx-auto text-primary" size={42} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Booking confirmed!</h1>
              <p className="text-sm text-muted-foreground mt-2">We've notified the car owner. Check your inbox for details.</p>

              {owner && (
                <div className="mt-6 p-4 bg-muted/50 rounded-xl text-left">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Owner contact</div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {owner.name?.charAt(0)?.toUpperCase() || "O"}
                    </div>
                    <div>
                      <div className="font-display font-semibold">{owner.name}</div>
                      <div className="text-xs text-muted-foreground">Verified owner</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {whatsappLink && (
                      <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-xl py-2.5 text-sm font-semibold transition-colors">
                        <MessageCircle size={16} /> WhatsApp
                      </a>
                    )}
                    {owner.phone && (
                      <a href={`tel:${owner.phone}`}
                        className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-xl py-2.5 text-sm font-semibold transition-colors">
                        <Phone size={16} /> {owner.phone}
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4 flex gap-3 justify-center">
                <Button asChild className="bg-primary hover:bg-primary/90"><Link to="/account">My bookings</Link></Button>
                <Button asChild variant="outline"><Link to="/cars">Find another car</Link></Button>
              </div>
            </>
          )}

          {status === "failed" && (
            <>
              <XCircle className="mx-auto text-destructive" size={42} />
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Payment failed</h1>
              <p className="text-sm text-muted-foreground mt-2">Your payment was not completed. Try again.</p>
              <div className="mt-6">
                <Button asChild className="bg-primary hover:bg-primary/90"><Link to={`/checkout/${bookingId}`}>Try again</Link></Button>
              </div>
            </>
          )}

        </Card>
      </div>
    </PublicLayout>
  );
}
